import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import { FiArrowLeft, FiCheckCircle, FiClock, FiFileText, FiCalendar, FiMail, FiExternalLink } from 'react-icons/fi';

const MyGraduateAdmissionsPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center text-uc-navy hover:text-uc-gold transition-colors mb-4"
          >
            <FiArrowLeft size={20} className="mr-2" />
            Back to Home
          </button>
          <h1 className="text-3xl font-bold text-uc-navy font-header">MY GRADUATE ADMISSIONS</h1>
          <p className="text-gray-600 mt-2">Track your graduate program applications</p>
        </div>

        {/* Application Status Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium uppercase">Applications</p>
                <p className="text-3xl font-bold text-uc-navy mt-2">2</p>
              </div>
              <FiFileText className="text-uc-gold" size={40} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium uppercase">Under Review</p>
                <p className="text-3xl font-bold text-uc-navy mt-2">1</p>
              </div>
              <FiClock className="text-uc-gold" size={40} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium uppercase">Accepted</p>
                <p className="text-3xl font-bold text-uc-navy mt-2">1</p>
              </div>
              <FiCheckCircle className="text-uc-gold" size={40} />
            </div>
          </div>
        </div>

        {/* My Applications */}
        <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-uc-navy">My Applications</h2>
            <p className="text-gray-600 text-sm mt-1">Current graduate program applications</p>
          </div>

          <div className="divide-y divide-gray-200">
            {/* Application 1 */}
            <div className="p-6 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold text-uc-navy">Ph.D. in Quantitative and Systems Biology</h3>
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      <FiCheckCircle size={16} />
                      Accepted
                    </span>
                  </div>
                  <p className="text-gray-600 mb-4">School of Natural Sciences</p>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500 font-medium">Application ID</p>
                      <p className="text-gray-900">QSB-2025-001234</p>
                    </div>
                    <div>
                      <p className="text-gray-500 font-medium">Submitted</p>
                      <p className="text-gray-900">December 1, 2024</p>
                    </div>
                    <div>
                      <p className="text-gray-500 font-medium">Decision Date</p>
                      <p className="text-gray-900">March 15, 2025</p>
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-900 font-medium mb-1">Congratulations! You have been accepted.</p>
                    <p className="text-sm text-green-800">
                      Please review your admission letter and complete enrollment by May 1, 2025.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Application 2 */}
            <div className="p-6 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold text-uc-navy">M.S. in Computer Science and Engineering</h3>
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                      <FiClock size={16} />
                      Under Review
                    </span>
                  </div>
                  <p className="text-gray-600 mb-4">School of Engineering</p>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500 font-medium">Application ID</p>
                      <p className="text-gray-900">CSE-2025-005678</p>
                    </div>
                    <div>
                      <p className="text-gray-500 font-medium">Submitted</p>
                      <p className="text-gray-900">January 15, 2025</p>
                    </div>
                    <div>
                      <p className="text-gray-500 font-medium">Expected Decision</p>
                      <p className="text-gray-900">April 1, 2025</p>
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-900">
                      Your application is currently under review by the admissions committee.
                      You will be notified via email when a decision is made.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Application Requirements */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-uc-navy mb-4">Application Requirements Checklist</h2>
          <p className="text-gray-600 mb-4">For Ph.D. in Quantitative and Systems Biology</p>

          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
              <FiCheckCircle className="text-green-600 flex-shrink-0" size={24} />
              <div>
                <p className="font-semibold text-green-900">Online Application</p>
                <p className="text-sm text-green-700">Submitted on December 1, 2024</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
              <FiCheckCircle className="text-green-600 flex-shrink-0" size={24} />
              <div>
                <p className="font-semibold text-green-900">Official Transcripts</p>
                <p className="text-sm text-green-700">Received from UC Berkeley</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
              <FiCheckCircle className="text-green-600 flex-shrink-0" size={24} />
              <div>
                <p className="font-semibold text-green-900">Letters of Recommendation (3)</p>
                <p className="text-sm text-green-700">All letters received</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
              <FiCheckCircle className="text-green-600 flex-shrink-0" size={24} />
              <div>
                <p className="font-semibold text-green-900">Statement of Purpose</p>
                <p className="text-sm text-green-700">Submitted</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
              <FiCheckCircle className="text-green-600 flex-shrink-0" size={24} />
              <div>
                <p className="font-semibold text-green-900">GRE Scores</p>
                <p className="text-sm text-green-700">Official scores received</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
              <FiCheckCircle className="text-green-600 flex-shrink-0" size={24} />
              <div>
                <p className="font-semibold text-green-900">Application Fee</p>
                <p className="text-sm text-green-700">Paid: $120.00</p>
              </div>
            </div>
          </div>
        </div>

        {/* Important Dates */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <FiCalendar className="text-uc-gold" size={24} />
            <h2 className="text-xl font-bold text-uc-navy">Important Dates</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border border-gray-200 rounded-lg">
              <p className="font-semibold text-uc-navy mb-1">Enrollment Deadline</p>
              <p className="text-gray-600 text-sm">May 1, 2025</p>
            </div>
            <div className="p-4 border border-gray-200 rounded-lg">
              <p className="font-semibold text-uc-navy mb-1">Orientation</p>
              <p className="text-gray-600 text-sm">August 18-20, 2025</p>
            </div>
            <div className="p-4 border border-gray-200 rounded-lg">
              <p className="font-semibold text-uc-navy mb-1">Classes Begin</p>
              <p className="text-gray-600 text-sm">August 25, 2025</p>
            </div>
            <div className="p-4 border border-gray-200 rounded-lg">
              <p className="font-semibold text-uc-navy mb-1">Financial Aid Deadline</p>
              <p className="text-gray-600 text-sm">April 15, 2025</p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <FiMail className="text-blue-600 mt-1 flex-shrink-0" size={24} />
            <div className="flex-1">
              <h3 className="font-bold text-blue-900 mb-2">Graduate Division Contact</h3>
              <p className="text-blue-800 mb-4">
                For questions about your application or admissions process:
              </p>
              <div className="space-y-2 text-sm text-blue-800">
                <p>• Email: graddiv@ucmerced.edu</p>
                <p>• Phone: (209) 228-4723</p>
                <p>• Office: 5200 North Lake Rd, Merced, CA 95343</p>
                <p>• Hours: Monday-Friday, 8:00 AM - 5:00 PM</p>
              </div>
              <a
                href="https://graduatedivision.ucmerced.edu"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-semibold text-sm"
              >
                Visit Graduate Division Website <FiExternalLink size={16} />
              </a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MyGraduateAdmissionsPage;
